import { ControlErrorStateMatcher } from './controlErrorStateMatcher';
import { HoursValidator } from '../../validators/hours-validator';
import { Component, Input, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { FormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { extractValidTime } from './extract-time';
import { TranslateService } from '@ngx-translate/core';
import { getLocale } from '@vendasta/galaxy/utility/locale';

export const TimePicker = {
  ALL_DAY: 'allDay',
  NOTES_ONLY: 'notesOnly',
};
export type TimePicker = (typeof TimePicker)[keyof typeof TimePicker];

@Component({
  selector: 'forms-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
})
export class TimePickerComponent implements OnInit, OnDestroy {
  @Input() control: UntypedFormControl;
  @Input() include24Hours = false;
  @Input() includeNotesOnly = false;
  @Input() placeholder = '';
  @Input() defaultTime = '00:00';
  @Input() includeIcon?: boolean = false;
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() label?: string;

  private options: string[] = [];

  controlErrorMatcher = new ControlErrorStateMatcher();
  @Output() hasAnyErrors = new EventEmitter();

  filteredOptions: Observable<string[]>;
  subscriptions: Subscription[] = [];
  innerControl: UntypedFormControl;

  constructor(
    private translateService: TranslateService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.innerControl = this.fb.control({}, this.control.validator);
    this.innerControl.setValue(this.control.value);

    this.innerControl.setValidators(Validators.compose([HoursValidator.HoursValidate]));
    this.innerControl.updateValueAndValidity();
    if (this.include24Hours) {
      this.options.push(TimePicker.ALL_DAY);
    }
    if (this.includeNotesOnly) {
      this.options.push(TimePicker.NOTES_ONLY);
    }
    for (const hour of [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12',
      '13',
      '14',
      '15',
      '16',
      '17',
      '18',
      '19',
      '20',
      '21',
      '22',
      '23',
    ]) {
      for (const minutes of ['00', '15', '30', '45']) {
        const time = `${hour}:${minutes}`;
        this.options.push(time);
      }
    }
    this.options.push(`23:59`);

    this.filteredOptions = this.innerControl.valueChanges.pipe(
      startWith(),
      map((timeDisplay) => (timeDisplay ? this.filter(timeDisplay) : this.options.slice())),
    );

    this.subscriptions.push(
      this.innerControl.valueChanges.subscribe((newVal) => {
        if (!newVal) {
          this.control.setValue(this.defaultTime);
          return;
        }
        if (newVal === TimePicker.ALL_DAY || newVal === TimePicker.NOTES_ONLY) {
          this.control.setValue(newVal);
          return;
        }
        const validTime = extractValidTime(newVal);
        if (!validTime || this.innerControl.invalid) {
          return;
        }
        this.control.setValue(validTime);
      }),
    );
  }

  filter(display: string): string[] {
    return this.options.filter((option) => this.displayFn(option).startsWith(display));
  }

  emitErrorMessage(error): string {
    if (error === null) {
      this.hasAnyErrors.emit(false);
    } else {
      this.hasAnyErrors.emit(true);
    }

    // this return statement is necessary for error message shown in the mat-error
    // component in mat-form-field in .html file of this component.
    return error ? this.translateService.instant('FRONTEND.FORMS.TIME_PICKER.ENTER_VALID_TIME') : '';
  }

  displayFn = (time: string): string => {
    if (!time) {
      return '';
    }

    if (time === TimePicker.ALL_DAY) {
      return this.translateService.instant('FRONTEND.FORMS.HOURS_OF_OPERATION.ALL_DAY');
    } else if (time === TimePicker.NOTES_ONLY) {
      return this.translateService.instant('FRONTEND.FORMS.HOURS_OF_OPERATION.NOTES_ONLY');
    }

    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    now.setHours(hours, minutes);

    const formatter = new Intl.DateTimeFormat(getLocale().code, { timeStyle: 'short' });

    return formatter.format(now);
  };
  ngOnDestroy(): void {
    this.subscriptions.forEach((subs) => subs.unsubscribe());
  }
}
